import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe } from 'lucide-react';
import { router, usePage } from '@inertiajs/react';
import { useTranslations } from '@/hooks/use-translations';

interface Language {
    code: string;
    name: string;
}

export function LanguageSelector() {
    const { locale, availableLanguages, routeSegments } = usePage().props as any;
    const { t } = useTranslations();
    const languages = availableLanguages || { en: 'English' };
    const currentLanguage = locale || 'en';

    // Helper function to get translated language name
    const getTranslatedLanguageName = (languageCode: string) => {
        return t(`lang.${languageCode}`) || languages[languageCode] || languageCode;
    };


    const handleLanguageChange = (languageCode: string) => {
        // Get current path
        const currentPath = window.location.pathname;
        
        if (currentPath.startsWith('/admin') || currentPath.startsWith('/api')) {
            // Don't change admin or API routes, just set cookie and reload
            document.cookie = `language=${languageCode};path=/;max-age=31536000`;
            router.reload({ preserveScroll: true });
            return;
        }
        
        try {
            // Set the language in cookie for persistence
            document.cookie = `language=${languageCode};path=/;max-age=31536000`;
            
            // Parse the current URL
            const pathSegments = currentPath.split('/').filter(Boolean);
            
            // Check if first segment is a locale
            const currentLocale = pathSegments[0];
            const isLocalePrefix = /^[a-z]{2}$/.test(currentLocale);
            
            let newPath;
            
            if (!isLocalePrefix || pathSegments.length === 0) {
                // No locale in path or root, redirect to language home
                newPath = `/${languageCode}`;
            } else if (pathSegments.length === 1) {
                // Just locale, redirect to new locale home
                newPath = `/${languageCode}`;
            } else {
                // We have a locale and route segments
                const currentRouteSegment = pathSegments[1]; // e.g., 'knjiga', 'book', 'zanr', 'genre'
                const remainingSegments = pathSegments.slice(2); // e.g., slug
                
                // Get route segments from shared data
                const currentSegments = routeSegments?.[currentLocale] || {};
                const targetSegments = routeSegments?.[languageCode] || {};
                
                // Create reverse mapping for current language
                const reverseCurrentSegments: Record<string, string> = {};
                for (const [key, value] of Object.entries(currentSegments)) {
                    reverseCurrentSegments[value as string] = key;
                }
                
                // Identify the route type from the current segment
                const routeType = reverseCurrentSegments[currentRouteSegment];
                
                if (routeType && targetSegments[routeType]) {
                    // Build the new path with translated segment
                    const translatedSegment = targetSegments[routeType];
                    newPath = `/${languageCode}/${translatedSegment}`;
                    
                    // Add any remaining segments (like slugs)
                    if (remainingSegments.length > 0) {
                        newPath += '/' + remainingSegments.join('/');
                    }
                } else {
                    // Can't identify route type, go to home
                    newPath = `/${languageCode}`;
                }
            }
            
            // Navigate to the new URL
            window.location.href = newPath;
        } catch (error) {
            console.error('Error changing language:', error);
            // Fallback to simple language change
            window.location.href = `/${languageCode}`;
        }
    };

    const currentLangName = getTranslatedLanguageName(currentLanguage);

    // Only show selector if there are translations available
    const languageEntries = Object.entries(languages);
    if (languageEntries.length <= 1) {
        return null; // Don't show language selector if only English is available
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                    <Globe className="h-4 w-4" />
                    <span>{currentLangName}</span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 max-h-96 overflow-y-auto">
                {languageEntries.map(([code, name]) => (
                    <DropdownMenuItem
                        key={code}
                        onClick={() => handleLanguageChange(code)}
                        className="justify-between"
                    >
                        <span>{getTranslatedLanguageName(code)}</span>
                        {code === currentLanguage && (
                            <span className="text-xs">✓</span>
                        )}
                    </DropdownMenuItem>
                ))}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}